import { MAX_FREE_UPLOADS } from '@listening/api-contract';
import { useFreeUploadWeekStartMs } from '@listening/frontend/hooks/use-free-upload-week-start';
import type { PlanKey, PortalFlow } from '@listening/api-contract/schemas';
import {
  apiMutation,
  apiQuery,
  idleQuery,
} from '@listening/frontend/query/api-runtime';
import {
  billingCancelEffect,
  billingChangePlanEffect,
  billingPreviewPlanChangeEffect,
  billingSubscribeEffect,
  billingPortalSessionEffect,
  Feedback,
} from '@listening/frontend/query/user';
import {
  billingStatusAtom,
  userKeys,
  userSummaryAtom,
} from '@listening/frontend/query/user-atoms';
import { recentUploadsAtom } from '@listening/frontend/query/audio-lists';
import { useAtomSet, useAtomValue } from '@effect/atom-react';
import { useSearch } from '@tanstack/react-router';
import { Duration, Effect } from 'effect';
import { AsyncResult, Atom, AtomRegistry, Reactivity } from 'effect/reactivity';
import { useEffect } from 'react';
import { useFreePlan } from '@/hooks/use-free-plan-availability';
import { rootLogger } from '@/lib/root-logger';
import { useFlag } from '@listening/frontend/analytics/client';
import {
  entitledFor,
  notEntitledFor,
  paywallShownFor,
} from './subscription-gate';
import { useIsUserLoggedIn } from './user-service';
import { useAuthSession } from './auth-service';

const moduleLogger = rootLogger.child({
  module: 'SubscriptionService',
});

// Auth-gated metering derivations — app-specific (gate on the app session hook),
// reading the shared billing-status atom.

/** `/billing/status` as the subscription readers in `subscription-gate.ts`
 * take it: the last value it had, and whether the latest read failed. */
export const billingLookupOf = <A, E>(
  result: AsyncResult.AsyncResult<A, E>,
) => ({
  data: AsyncResult.getOrElse(result, () => undefined),
  isError: AsyncResult.isFailure(result),
});

const useBillingStatus = () => {
  const userLoggedIn = useIsUserLoggedIn();
  return useAtomValue(userLoggedIn ? billingStatusAtom : idleQuery);
};

/**
 * False until /billing/status has settled (session resolving, query in flight,
 * or errored). Safe for restricting free users, since it never flags a user
 * as free-tier prematurely.
 */
export const useIsInvalidSubscription = () =>
  useBillingStatus().pipe(billingLookupOf, notEntitledFor);

/**
 * Whether the paywall counts as shown — not subscribed as far as we can tell,
 * or a lookup that failed. `not-shown` while the session resolves or the query
 * is in flight.
 *
 * Deliberately separate from [useIsInvalidSubscription], which must stay
 * conservative: it gates access to paid features, so a blip on /billing/status
 * cannot be allowed to revoke them. This one only reports what the user is
 * looking at, where a failed lookup means the paywall is on screen and going
 * quiet would undercount it. See `premium_paywall_interaction.opened`.
 */
export const usePaywallShown = () =>
  useBillingStatus().pipe(billingLookupOf, paywallShownFor);

/**
 * Also false until /billing/status has settled, so a premium user reads as free
 * during initial load. Reactive renders self-correct once data lands, but
 * point-in-time decisions (event handlers) must NOT gate on this — settle the
 * billing-status read first (see `onDrop` in file-upload-service.ts).
 */
export const useIsValidSubscription = () =>
  useBillingStatus().pipe(billingLookupOf, entitledFor);

const useFreePlanRestrictions = () => {
  const { isInFreePlanCountry, isResolvingCountry } = useFreePlan();
  const billing = useBillingStatus();
  const isSubscriptionInvalid = billing.pipe(billingLookupOf, notEntitledFor);

  const { value: ffBypassUploadLimit, isLoading: isResolvingUploadFlag } =
    useFlag('bypassClientUploadPrecheck');
  const { value: ffBypassPrivateAudioLimit } = useFlag(
    'bypassFreePlanPrivateAudioLimit',
  );

  return {
    uploadLimitReady:
      !AsyncResult.isInitial(billing) &&
      !isResolvingCountry &&
      !isResolvingUploadFlag,
    uploadLimitEnabled:
      isSubscriptionInvalid &&
      !isInFreePlanCountry &&
      !isResolvingCountry &&
      ffBypassUploadLimit !== 'true',
    privateAudioLimitEnabled:
      isSubscriptionInvalid && ffBypassPrivateAudioLimit !== 'true',
  };
};

// Canceling, changing plan or uncanceling flips the subscription and
// `metering.is_active_subscription` on the summary. Both are cached for an
// hour, so without refetching them the user keeps seeing the old plan — and
// stays gated out of paid features (see `useIsInvalidSubscription`) — for up
// to an hour.
const billingWriteKeys = [userKeys.billing, userKeys.summary];

/**
 * The legacy endpoint accepted `{cancelReason, likedFeatures}` as a free-form
 * body and silently dropped likedFeatures server-side. The typed Effect
 * contract only accepts `{reason?: string}`, so likedFeatures goes through the
 * dedicated PostCancelSurvey endpoint (which already exists for the
 * delete-account flow).
 */
export const cancelStripeAtom = apiMutation(
  (params: { cancelReason: string; likedFeatures: string }) =>
    Effect.gen(function* () {
      // The survey is fire-and-forget analytics that must not block the
      // cancel: fork it as a daemon so it runs independently, and log any
      // failure through Effect logging (not the deprecated pino logger).
      yield* Effect.forkDetach(
        Feedback.submitCancellationSurvey({
          is_user_deleted: false,
          user_cancel_reason: params.cancelReason,
          user_liked_features: params.likedFeatures,
        }).pipe(
          Effect.catch((error) =>
            Effect.logError('PostCancelSurvey failed (non-blocking)', error),
          ),
        ),
      );
      const result = yield* billingCancelEffect({
        reason: params.cancelReason,
      });
      yield* Effect.logInfo('Stripe subscription canceled');
      return result;
    }),
  { reactivityKeys: billingWriteKeys },
);

/**
 * What a plan change would cost, read once per confirmation.
 *
 * Uncached on purpose — never fresh, dropped as soon as the dialog closes, and
 * never retried. The amount this returns is the amount the confirm is held to,
 * so a second reader must get a second computation rather than the one shown
 * minutes ago, and a preview left in the cache is one the server would refuse.
 * It is a POST, so it is never sent twice: a failed preview is a sentence the
 * user reads and a button they press again, not a background recovery.
 */
export const planChangePreviewAtom = Atom.family((planId: PlanKey) =>
  apiQuery(billingPreviewPlanChangeEffect({ plan_id: planId }), {
    timeToLive: Duration.zero,
  }),
);

/**
 * Move to another catalog plan.
 *
 * Takes a `PlanKey`, never a price id: the server resolves the price by lookup
 * key, so a price experiment or a repricing needs no client deploy, and naming
 * an uncatalogued price is unrepresentable rather than merely rejected.
 *
 * The proration instant and the amount come from the preview the person
 * confirmed: the server recomputes against them and refuses when the cost has
 * moved, so no request here can charge an amount that was never shown.
 */
export const changePlanAtom = apiMutation(
  (params: {
    planId: PlanKey;
    prorationDate: string;
    expectedDueTodayCents: number;
    idempotencyKey: string;
  }) =>
    billingChangePlanEffect(
      {
        plan_id: params.planId,
        proration_date: params.prorationDate,
        expected_due_today_cents: params.expectedDueTodayCents,
      },
      params.idempotencyKey,
    ).pipe(Effect.tap(() => Effect.logInfo('Stripe subscription modified'))),
  { reactivityKeys: billingWriteKeys },
);

/**
 * Clear a pending cancellation — the uncancel arm of `POST /billing/subscribe`.
 *
 * Posts the plan the user is already on, because the endpoint's uncancel branch
 * matches on the live subscription, not on the request. A response carrying a
 * `client_secret` means a card is needed after all — an owed-time resubscribe's
 * SetupIntent, or a fresh checkout — and the caller routes it by
 * `confirmation_type`.
 */
export const uncancelAtom = apiMutation(
  (params: { planId: PlanKey; uncancelOnly: boolean }) =>
    billingSubscribeEffect({
      plan_id: params.planId,
      ...(params.uncancelOnly ? { uncancel_only: true } : {}),
    }),
  { reactivityKeys: billingWriteKeys },
);

/**
 * Mint a billing-portal session and hand the browser to Stripe.
 *
 * Stripe's portal collects the card and carries invoice history, which replaces
 * the bespoke card form this app used to embed. Minted on click because a portal
 * session expires five minutes after it is created. One per flow, so each
 * button shows its own progress.
 */
export const portalSessionAtom = Atom.family((flow: PortalFlow) =>
  apiMutation(() =>
    billingPortalSessionEffect({ flow }).pipe(
      Effect.tap((session) =>
        Effect.sync(() => {
          window.location.href = session.url;
        }),
      ),
      Effect.tapError((error) => Effect.sync(() => moduleLogger.error(error))),
    ),
  ),
);

/**
 * Drop every read taken before a purchase settled — billing status, the user
 * summary, and the subscription/payment lists.
 *
 * Awaiting the result waits for billing status and the summary to refetch,
 * which is what a checkout must do before it navigates: the page it leaves for
 * was primed while the buyer was still unentitled, and `/billing/status` is
 * cached for an hour, so without this they land on a paid page that still
 * reads them as free tier — or get bounced back to the paywall by the
 * dashboard's entitlement guard.
 */
export const settleBillingAtom = apiMutation(() =>
  Effect.gen(function* () {
    yield* Reactivity.invalidate(billingWriteKeys);
    const registry = yield* AtomRegistry.AtomRegistry;
    const settled = { suspendOnWaiting: true };
    yield* Effect.all(
      [
        AtomRegistry.getResult(registry, billingStatusAtom, settled).pipe(
          Effect.ignore,
        ),
        AtomRegistry.getResult(registry, userSummaryAtom, settled).pipe(
          Effect.ignore,
        ),
      ],
      { concurrency: 'unbounded', discard: true },
    );
  }),
);

export const useSettleBillingCaches = () =>
  useAtomSet(settleBillingAtom, { mode: 'promise' });

/**
 * Cleans up after a Stripe redirect return, on `/post-purchase`.
 *
 * The **write** is the route loader's, not this hook's: it calls
 * `POST /billing/confirm` during SSR, which syncs from Stripe, stores a
 * SetupIntent's card as the default and marks the user as having paid. By the
 * time this runs the truth is already settled server-side, so all that is left
 * is to strip the intent ids out of the URL and drop the reads that were taken
 * before the purchase.
 *
 * The "Complete setup" dialog that used to finish an `incomplete` subscription
 * from the plan page is gone — the paywall is the resume path — and it was the
 * only flow that landed a `payment_intent` anywhere but here.
 */
export const useCheckoutReturnCleanup = () => {
  const settleBillingCaches = useAtomSet(settleBillingAtom);
  const search: {
    payment_intent?: string;
    setup_intent?: string;
  } = useSearch({ strict: false });

  useEffect(() => {
    if (!search.payment_intent && !search.setup_intent) return;

    moduleLogger.info(
      {
        payment_intent: search.payment_intent,
        setup_intent: search.setup_intent,
      },
      'Stripe redirect post-purchase cleanup',
    );

    window.history.replaceState({}, '', window.location.pathname);

    settleBillingCaches();
  }, [settleBillingCaches, search.payment_intent, search.setup_intent]);
};

export const useFreePlanUploadLimit = () => {
  // One bounded page of non-errored audios (server except_status=Error,
  // sort=created_at). The week-start filter below counts this week's uploads;
  // a free user with >1 page of uploads in a week is already over the limit.
  const uploads = useAtomValue(recentUploadsAtom);
  const audioAndQueue = AsyncResult.getOrElse(uploads, () => []);
  const { user } = useAuthSession();
  const lastResetMs = useFreeUploadWeekStartMs();

  const uploadsSinceReset = audioAndQueue.filter(
    (audio) =>
      audio.owner_id === user?.userId && audio.created_at_ms >= lastResetMs,
  );
  const maxUploads = MAX_FREE_UPLOADS;
  const remainingUploads = Math.max(maxUploads - uploadsSinceReset.length, 0);
  const { uploadLimitEnabled, uploadLimitReady } = useFreePlanRestrictions();

  return {
    remainingUploads,
    maxUploads,
    overUploadLimit: remainingUploads <= 0 && uploadLimitEnabled,
    limitEnabled: uploadLimitEnabled,
    isReady:
      uploadLimitReady &&
      (!uploadLimitEnabled || !AsyncResult.isInitial(uploads)),
  };
};

export const useFreePlanPrivateAudioLimit = () => {
  const { privateAudioLimitEnabled } = useFreePlanRestrictions();
  return {
    limitEnabled: privateAudioLimitEnabled,
  };
};
